import React, { createContext, useState, useEffect } from 'react';

import { ptBR } from '@material-ui/core/locale';

import {
	createTheme,
	ThemeProvider as MaterialThemeProvider,
} from '@material-ui/core/styles';

import { useLocalStorage } from '../../hooks/useLocalStorage';
import whatsBackground from '../../assets/wa-background.png';
import whatsBackgroundDark from '../../assets/wa-background-dark.jpg';

const ThemeContext = createContext();

function ThemeProvider({ children }) {
	const [locale, setLocale] = useState();
	const [darkMode, setDarkMode] = useLocalStorage('darkMode', false);
	const palletType = darkMode ? 'dark' : 'light';

	useEffect(() => {
		const i18nlocale = localStorage.getItem('i18nextLng');
		const browserLocale = i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

		if (browserLocale === 'ptBR') {
			setLocale(ptBR);
		}
	}, []);

	const darkFormOverrides = darkMode
		? {
				MuiDialog: {
					paper: {
						backgroundColor: '#252829',
						color: '#e8eaed',
					},
				},
				MuiDialogTitle: {
					root: {
						color: '#e8eaed',
					},
				},
				MuiDialogContent: {
					root: {
						color: '#e8eaed',
						borderColor: 'rgba(255,255,255,0.08)',
					},
				},
				MuiDialogActions: {
					root: {
						borderTop: '1px solid rgba(255,255,255,0.08)',
					},
				},
				MuiOutlinedInput: {
					root: {
						backgroundColor: 'rgba(255,255,255,0.06)',
						color: '#e8eaed',
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: 'rgba(255,255,255,0.24)',
						},
						'&:hover .MuiOutlinedInput-notchedOutline': {
							borderColor: 'rgba(255,255,255,0.38)',
						},
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: '#4db6ac',
							borderWidth: 2,
						},
					},
					input: {
						color: '#e8eaed',
						'&:-webkit-autofill': {
							WebkitBoxShadow: '0 0 0 1000px #2d3033 inset',
							WebkitTextFillColor: '#e8eaed',
							caretColor: '#e8eaed',
						},
					},
				},
				MuiInputLabel: {
					outlined: {
						color: 'rgba(255,255,255,0.72)',
						'&$focused': {
							color: '#80cbc4',
						},
					},
				},
				MuiFormHelperText: {
					root: {
						color: 'rgba(255,255,255,0.55)',
					},
				},
				MuiSelect: {
					icon: {
						color: 'rgba(255,255,255,0.72)',
					},
				},
				MuiMenu: {
					paper: {
						backgroundColor: '#2d3033',
						color: '#e8eaed',
					},
				},
				MuiMenuItem: {
					root: {
						color: '#e8eaed',
					},
				},
			}
		: {};

	const theme = createTheme(
		{
			overrides: {
				MuiCssBaseline: {
					'@global': {
						'*::-webkit-scrollbar': {
							width: '8px',
							height: '8px',
						},
						'*::-webkit-scrollbar-thumb': {
							backgroundColor: darkMode
								? 'hsla(0,0%,100%,.16)'
								: 'rgba(0,0,0,.1)',
						},
					},
				},
				...darkFormOverrides,
			},

			palette: {
				type: palletType,
				...(darkMode
					? {
							text: {
								primary: '#e8eaed',
								secondary: 'rgba(232, 234, 237, 0.7)',
								disabled: 'rgba(232, 234, 237, 0.38)',
								hint: 'rgba(232, 234, 237, 0.5)',
							},
							divider: 'rgba(255, 255, 255, 0.12)',
							action: {
								active: 'rgba(255,255,255,0.72)',
								hover: 'rgba(255,255,255,0.08)',
								selected: 'rgba(255,255,255,0.12)',
								disabled: 'rgba(255,255,255,0.26)',
								disabledBackground: 'rgba(255,255,255,0.08)',
							},
						}
					: {}),
				background: {
					paper: darkMode ? '#1b1d1d' : "#F7F5F3",
					default: darkMode ? '#1b1d1d' : '#ffffff',
				},
				primary: {
					main: darkMode ? '#eeeeee' : '#808080',
				},
				secondary: {
					main: darkMode ? '#eeeeee' : '#808080',
				},
				error: {
					50: '#e0f2f1',
					100: '#b2dfdb',
					200: '#80cbc4',
					300: '#4db6ac',
					400: '#26a69a',
					500: '#009688',
					600: '#00897b',
					700: '#00796b',
					800: '#00695c',
					900: '#004d40',
					A100: '#a7ffeb',
					A200: '#64ffda',
					A400: '#1de9b6',
					A700: '#00bfa5',
				},
			},
			mainWrapper: {
				background: {
					default: darkMode ? '#1b1d1d' : '#F5F1EB',
				},
				backgroundImage: darkMode
					? `url(${whatsBackgroundDark})`
					: `url(${whatsBackground})`,
			}
			,

			backgroundImage: darkMode
				? `url(${whatsBackgroundDark})`
				: `url(${whatsBackground})`,

			typography: {
				fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
			},

			messagesList: {
				messageLeft: {
					backgroundColor: darkMode ? '#202c33' : '#FFFFFF',
					color: darkMode ? '#FFFFFF' : '#303030',
				},
				messageRight: {
					backgroundColor: darkMode ? '#005c4b' : '#d9fdd3',
					color: darkMode ? '#FFFFFF' : '#303030',
				},
				backgroundColor: darkMode ? '#222222' : '#E5DDD5',
				timestampBackground: darkMode ? '#1E2A30' : '#E1F3FB',
				noteMessage: darkMode ? '#4D6C7C' : '#FFFFD4',
			},
		},
		locale,
	);

	return (
		<ThemeContext.Provider value={{ darkMode, setDarkMode }}>
			<MaterialThemeProvider theme={theme}>{children}</MaterialThemeProvider>
		</ThemeContext.Provider>
	);
}

export { ThemeContext, ThemeProvider };
